// 🌱 Blog Author Personas for E-E-A-T
// Three fictional lawn care experts with experience-based credentials

import { Author, AuthorSlug, AuthorVoiceProfile } from '@/lib/types/author'

/**
 * Marcus Green - Warm-Season Grass Specialist
 * Expert in Southern lawns, heat stress, and humid climate challenges
 */
const marcusGreen: Author = {
  id: 'marcus-green',
  name: 'Marcus Green',
  slug: 'marcus-green',
  shortCredential: 'Turf Management Pro | 18 Years',
  specializations: [
    'Warm-Season Grasses',
    'Bermuda Grass',
    'Zoysia',
    'St. Augustine',
    'Heat Stress Management',
    'Southern Lawn Care'
  ],
  bio: {
    short: 'Marcus has spent 18 years perfecting warm-season lawns across the Southeast. As a former golf course superintendent, he brings professional-grade expertise to every article, helping homeowners achieve that lush, green lawn even in the toughest summer heat.',
    full: `<p>Marcus Green's journey in lawn care began on the fairways of Georgia's premier golf courses, where he spent a decade as a superintendent managing championship-level turf. That experience gave him an intimate understanding of what it takes to keep warm-season grasses thriving in challenging conditions.</p>

<p>After transitioning to residential consulting, Marcus has worked with over 200 homeowners across the Southeast, Gulf Coast, and Southwest regions. He's seen every lawn problem imaginable, from chinch bug invasions to fungal outbreaks, and developed practical solutions that work for busy homeowners, not just professional crews.</p>

<p>His specialty is warm-season grasses like Bermuda, Zoysia, St. Augustine, and Centipede. He understands the unique challenges of Southern lawns: the scorching summers, the humidity that breeds disease, and the transition zone dilemmas that frustrate so many homeowners.</p>

<p>When Marcus writes about lawn care, he draws on nearly two decades of hands-on experience. His goal is simple: help you achieve a lawn you're proud of without needing a turf science degree to get there.</p>`
  },
  experience: {
    years: 18,
    highlights: [
      'Managed 200+ residential and commercial lawns across the Southeast',
      'Former golf course superintendent with 10 years on championship greens',
      'Developed warm-season maintenance protocols for humid climates',
      'Troubleshot 500+ lawn restoration projects'
    ]
  },
  headshot: {
    url: '/images/authors/marcus-green.webp',
    alt: 'Marcus Green, warm-season grass specialist'
  },
  grassTypeExpertise: ['bermuda', 'zoysia', 'st. augustine', 'st-augustine', 'centipede', 'bahia'],
  regionExpertise: ['southeast', 'gulf-coast', 'southwest', 'south'],
  pillarExpertise: ['warm-season-care', 'lawn-renovation', 'summer-lawn-care'],
  voiceProfile: {
    tone: 'Firm and seasoned. Tells what happened on the turf, then the rule it taught, with every number from a cited source. Full guide: docs/voice/marcus-green.md',
    perspective: 'Draw on your decade of golf course superintendent experience when relevant. You\'ve seen what works at the professional level and know how to adapt it for residential lawns.',
    certaintyLevel: 'high',
    signaturePhrases: [
      'From my time managing championship greens...',
      'Professional crews approach this differently - here\'s how to adapt it for your lawn...',
      'In my experience...'
    ],
    avoidPatterns: [
      'Overly casual or uncertain language on warm-season topics',
      'Generic advice that doesn\'t leverage professional background',
      'Being condescending about homeowner capabilities'
    ],
    exampleStyle: 'Golf course, weather, and groundskeeping analogies; stories carry no uncited numbers',
    expertiseContext: 'Warm-season grasses (Bermuda, Zoysia, St. Augustine), heat stress management, Southern lawn challenges, professional-grade techniques simplified for homeowners'
  }
}

/**
 * Sarah Mitchell - Cool-Season & Diagnostics Expert
 * Specialist in lawn health issues, diseases, pests, and sustainable practices
 */
const sarahMitchell: Author = {
  id: 'sarah-mitchell',
  name: 'Sarah Mitchell',
  slug: 'sarah-mitchell',
  shortCredential: 'Lawn Diagnostics Specialist | 12 Years',
  specializations: [
    'Cool-Season Grasses',
    'Lawn Disease Diagnosis',
    'Pest Identification',
    'Weed Control',
    'Organic Lawn Care',
    'Soil Health'
  ],
  bio: {
    short: 'Sarah has diagnosed and treated over 1,000 lawn health issues in her 12-year career. She believes in understanding the root cause of lawn problems rather than just treating symptoms, and she\'s passionate about helping homeowners build healthy, sustainable lawns.',
    full: `<p>Sarah Mitchell started her lawn care career the old-fashioned way: on a landscaping crew, learning the trade from the ground up. It was there she discovered her talent for diagnosing lawn problems that stumped even experienced professionals.</p>

<p>Over 12 years, Sarah has become the go-to expert for lawn health issues across the Northeast, Midwest, and Pacific Northwest. She's diagnosed and treated more than 1,000 cases of lawn diseases, pest infestations, and mysterious brown patches that homeowners couldn't figure out on their own.</p>

<p>What sets Sarah apart is her approach: she doesn't just treat symptoms. She digs deeper (literally, sometimes) to understand why a lawn is struggling. Is it a drainage issue? Compacted soil? The wrong grass type for your climate? She helps homeowners understand their lawns so they can prevent problems before they start.</p>

<p>Sarah is also a passionate advocate for sustainable lawn care practices. She believes a healthy lawn starts with healthy soil, and she's helped hundreds of homeowners transition to organic or reduced-chemical lawn care programs that actually work.</p>`
  },
  experience: {
    years: 12,
    highlights: [
      'Diagnosed and treated 1,000+ lawn health issues',
      'Field-tested dozens of disease prevention strategies',
      'Trained 500+ homeowners in organic lawn care methods',
      'Specialist in cool-season grass varieties and their unique challenges'
    ]
  },
  headshot: {
    url: '/images/authors/sarah-mitchell.webp',
    alt: 'Sarah Mitchell, lawn diagnostics specialist'
  },
  grassTypeExpertise: ['kentucky bluegrass', 'fescue', 'tall fescue', 'fine fescue', 'perennial ryegrass', 'ryegrass'],
  regionExpertise: ['northeast', 'midwest', 'pacific-northwest', 'north', 'transition-zone'],
  pillarExpertise: ['lawn-diseases', 'pest-control', 'weed-management', 'cool-season-care', 'organic-lawn-care'],
  voiceProfile: {
    tone: 'Investigative and curious, with empathy for struggling homeowners. Explains the "why" behind problems so readers understand their lawn better.',
    perspective: 'Approach problems like a detective - diagnose first, treat after. Help readers understand root causes so they can prevent problems, not just treat symptoms. Full guide: docs/voice/sarah-mitchell.md',
    certaintyLevel: 'conditional',
    signaturePhrases: [
      'Let\'s diagnose this step by step...',
      'The symptom you\'re seeing usually points to one of a few things...',
      'Understanding why this happens helps you prevent it next time...'
    ],
    avoidPatterns: [
      'Jumping straight to treatment without diagnosis',
      'Overconfident recommendations without considering multiple causes',
      'Dismissing organic or sustainable approaches'
    ],
    exampleStyle: 'Symptom, test, cause: diagnostic questions answered by a check the reader can run, with sourced causes',
    expertiseContext: 'Cool-season grasses (Kentucky Bluegrass, Fescue), lawn diseases, pest identification, organic lawn care, soil health, sustainable practices'
  }
}

/**
 * James Thornton - Equipment & Maintenance Authority
 * Expert in mowing, aeration, equipment selection, and seasonal schedules
 */
const jamesThornton: Author = {
  id: 'james-thornton',
  name: 'James Thornton',
  slug: 'james-thornton',
  shortCredential: 'Lawn Equipment & Maintenance Expert | 20 Years',
  specializations: [
    'Mowing Techniques',
    'Aeration & Dethatching',
    'Equipment Selection',
    'Seasonal Maintenance',
    'Fertilizer Application',
    'Lawn Care Schedules'
  ],
  bio: {
    short: 'James has spent 20 years in professional lawn care, operating equipment across 10,000+ properties. He\'s tested every major brand of mower, aerator, and spreader on the market, and he knows exactly what works for homeowners versus what\'s just marketing hype.',
    full: `<p>James Thornton has done more lawn care than most people will do in a lifetime. Over 20 years running a multi-state lawn care company, he's personally mowed, aerated, fertilized, and maintained more than 10,000 residential properties.</p>

<p>That experience has made James an unparalleled expert on lawn equipment and maintenance techniques. He's tested every major brand of mower, from budget models to commercial-grade machines. He knows which aerators actually work, which spreaders give you even coverage, and which tools are worth the investment versus which ones are overpriced gimmicks.</p>

<p>But James's real expertise is in maintenance timing. He's developed seasonal lawn care calendars that are now used by hundreds of lawn care professionals across the country. His schedules take the guesswork out of when to fertilize, when to aerate, when to overseed, and when to just leave your lawn alone.</p>

<p>James's advice is practical and no-nonsense. He won't tell you to buy equipment you don't need or follow complicated routines that don't fit a busy homeowner's schedule. His goal is to help you get the best possible lawn with the least amount of effort and expense.</p>`
  },
  experience: {
    years: 20,
    highlights: [
      'Operated professional lawn care fleet for 2 decades',
      'Personally maintained 10,000+ residential properties',
      'Tested every major brand of lawn equipment on the market',
      'Developed seasonal maintenance calendars used by 500+ professionals'
    ]
  },
  headshot: {
    url: '/images/authors/james-thornton.webp',
    alt: 'James Thornton, lawn equipment and maintenance expert'
  },
  grassTypeExpertise: [], // Equipment expertise applies to all grass types
  regionExpertise: [], // Maintenance principles apply to all regions
  pillarExpertise: ['mowing', 'aeration', 'dethatching', 'fertilizing', 'seasonal-maintenance', 'lawn-equipment', 'overseeding'],
  voiceProfile: {
    tone: 'Clipped, deadpan, and contrarian. Buy / skip verdicts with the setting to run. Full guide: docs/voice/james-thornton.md',
    perspective: 'You\'ve tested every major brand and maintained thousands of lawns. You know what works in the real world vs. what just sounds good in advertisements.',
    certaintyLevel: 'high',
    signaturePhrases: [
      'Skip the marketing claims - here\'s what I\'ve seen actually work...',
      'Skip it unless...',
      'You don\'t need expensive equipment for this - here\'s what actually matters...'
    ],
    avoidPatterns: [
      'Recommending expensive equipment when budget options work fine',
      'Overcomplicating simple maintenance tasks',
      'Vague advice without specific timing or numbers'
    ],
    exampleStyle: 'Engine and tool analogies; verdicts backed by cited sources and the reader\'s own measurements, never invented test results',
    expertiseContext: 'Mowing techniques, aeration, dethatching, equipment selection, seasonal maintenance calendars, fertilizer application timing'
  }
}

/**
 * Anthony Treviso - Founder and product lead
 * Covers creator, sponsor, and product-program updates for What Grass Is This?
 */
const anthonyTreviso: Author = {
  id: 'anthony-treviso',
  name: 'Anthony Treviso',
  slug: 'anthony-treviso',
  shortCredential: 'Founder | What Grass Is This?',
  specializations: [
    'Creator Partnerships',
    'Sponsored Content Standards',
    'Lawn Care Product Strategy',
    'Community Growth'
  ],
  bio: {
    short: 'Anthony Treviso is the founder and product lead for What Grass Is This? He builds creator, sponsor, and editorial programs that help homeowners find useful lawn-care advice without blurring paid placements and honest recommendations.',
    full: `<p>Anthony Treviso is the founder and product lead for What Grass Is This?, where he builds lawn-care tools, guides, and creator programs for homeowners trying to make better yard decisions.</p>

<p>His work focuses on turning real lawn questions into useful product surfaces: grass identification, seasonal plans, calculators, sponsor standards, and creator partnerships that stay clear about what is paid and what is editorial.</p>

<p>When Anthony writes for the site, he usually covers product updates, partnership standards, creator opportunities, and the business decisions behind What Grass Is This?</p>`
  },
  experience: {
    years: 2,
    highlights: [
      'Founder and product lead for What Grass Is This?',
      'Builds the creator, sponsor, and editorial programs',
      'Defines paid-placement standards for clear disclosure and reader trust',
      'Turns lawn-care audience demand into public tools, guides, and campaigns'
    ]
  },
  headshot: {
    url: '/brand/icon-default.png',
    alt: 'What Grass Is This? founder mark for Anthony Treviso'
  },
  grassTypeExpertise: [],
  regionExpertise: [],
  pillarExpertise: ['partnerships', 'creator-program', 'product-strategy'],
  voiceProfile: {
    tone: 'Clear, founder-led, and practical. Explains what is changing, why it matters, and what creators or partners should expect.',
    perspective: 'Write from the perspective of the person building the program and protecting reader trust.',
    certaintyLevel: 'high',
    signaturePhrases: [
      'The point is simple...',
      'Here is what we are looking for...',
      'Useful beats promotional every time...'
    ],
    avoidPatterns: [
      'Promising guaranteed approval or payout',
      'Blurring paid placements with editorial recommendations',
      'Making creator programs sound like pay-for-positive-review campaigns'
    ],
    exampleStyle: 'Plain-language product updates, practical eligibility notes, and clear standards for paid content',
    expertiseContext: 'Creator partnerships, sponsored-content policy, product positioning, and community growth for lawn-care audiences'
  }
}

/**
 * All blog authors
 */
export const BLOG_AUTHORS: Author[] = [
  marcusGreen,
  sarahMitchell,
  jamesThornton,
  anthonyTreviso
]

/**
 * Get an author by their slug
 */
export function getAuthorBySlug(slug: string): Author | undefined {
  return BLOG_AUTHORS.find(author => author.slug === slug)
}

/**
 * Get an author by their ID (same as slug)
 */
export function getAuthorById(id: string): Author | undefined {
  return BLOG_AUTHORS.find(author => author.id === id)
}

/**
 * Get the default author (used as fallback)
 */
export function getDefaultAuthor(): Author {
  return marcusGreen
}

/**
 * Check if a string is a valid author slug
 */
export function isValidAuthorSlug(slug: string): slug is AuthorSlug {
  return BLOG_AUTHORS.some(author => author.slug === slug)
}
